import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core'
import { Subscription } from 'rxjs'
import Keyboard from 'simple-keyboard'
import { VirtualKeyboardService } from './virtual-keyboard.service'
import { VirtualKeyboardTarget } from './virtual-keyboard.types'
import {
  KEYBOARD_DISPLAY,
  KEYBOARD_LAYOUTS,
  KEY_CLEAR,
  KEY_SHIFT,
} from './virtual-keyboard.layouts'

/**
 * Teclado virtual do totem (simple-keyboard, MIT).
 *
 * Renderizado UMA vez no container do autoatendimento e compartilhado por todas
 * as telas do fluxo; aparece ancorado na base da tela quando um campo com
 * `appVirtualKeyboard` recebe foco. Publica a própria altura no serviço para que
 * as telas afastem campo e botão de confirmação — o teclado nunca cobre o que o
 * paciente está preenchendo.
 */
@Component({
  selector: 'app-virtual-keyboard',
  templateUrl: './virtual-keyboard.component.html',
  styleUrls: ['./virtual-keyboard.component.scss'],
})
export class VirtualKeyboardComponent implements AfterViewInit, OnDestroy {
  @ViewChild('keyboardHost') keyboardHost?: ElementRef<HTMLDivElement>
  @ViewChild('panel') panel?: ElementRef<HTMLDivElement>

  target: VirtualKeyboardTarget | null = null

  private keyboard?: Keyboard
  /** Elemento em que o teclado atual foi montado. */
  private attachedHost?: HTMLDivElement
  private subscription = new Subscription()
  /** Evita que o próprio clique no teclado tire o foco/valor do campo. */
  private syncing = false

  constructor(
    private keyboardService: VirtualKeyboardService,
    private changeDetector: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.subscription.add(
      this.keyboardService.target$.subscribe((target) => {
        this.target = target
        this.changeDetector.detectChanges()
        target ? this.attachTo(target) : this.detach()
      })
    )
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
    this.destroyKeyboard()
    this.keyboardService.setHeight(0)
  }

  /**
   * Fecha o teclado — botão "Pronto".
   *
   * Tira o foco do campo ANTES de fechar. O painel cancela o mousedown para o
   * campo não perder o foco a cada tecla, e o efeito colateral era o campo nunca
   * ficar "visitado": o Material só desenha a mensagem de erro em campo visitado,
   * então o paciente digitava um valor inválido, tocava em Pronto e não via
   * mensagem nenhuma — só o botão de confirmar apagado, sem explicação. Tocar em
   * Pronto é o paciente dizendo que terminou naquele campo.
   */
  close(): void {
    this.target?.input.blur()
    this.keyboardService.close()
  }

  /**
   * Impede que o mousedown no teclado roube o foco do input: sem isso, o campo
   * perde o foco a cada tecla e o cursor "pisca" fora do campo no totem.
   */
  preventBlur(event: MouseEvent): void {
    event.preventDefault()
  }

  private attachTo(target: VirtualKeyboardTarget): void {
    const layout = KEYBOARD_LAYOUTS[target.layout] || KEYBOARD_LAYOUTS.alphanumeric

    // Se o painel foi recriado, o host anterior saiu da pagina: recria o teclado
    // nele em vez de escrever num no orfao.
    if (this.keyboard && !this.isAttachedToCurrentHost()) {
      this.destroyKeyboard()
    }

    if (!this.keyboard) {
      this.createKeyboard()
    }

    this.keyboard?.setOptions({
      layout: layout,
      layoutName: 'default',
      display: KEYBOARD_DISPLAY,
      maxLength: target.maxLength,
    })
    this.setKeyboardValue(target.input.value || '')

    this.publishHeight()
    this.scrollTargetIntoView(target)
  }

  /**
   * Ao fechar, o *ngIf remove o painel e, com ele, o elemento onde o
   * simple-keyboard montou. A instancia precisa morrer junto: mantida viva, a
   * proxima abertura escreveria num no ja fora da pagina e o teclado apareceria
   * sem nenhuma tecla — foi o que aconteceu ao trocar de tela no meio do fluxo.
   */
  private detach(): void {
    this.destroyKeyboard()
    this.keyboardService.setHeight(0)
  }

  private createKeyboard(): void {
    if (!this.keyboardHost) {
      return
    }

    // simple-keyboard dispara eventos fora do Angular; roda o handler dentro da
    // zona para o binding do campo atualizar imediatamente.
    this.zone.runOutsideAngular(() => {
      this.attachedHost = this.keyboardHost!.nativeElement
      this.keyboard = new Keyboard(this.keyboardHost!.nativeElement, {
        onChange: (input: string) => this.zone.run(() => this.onChange(input)),
        onKeyPress: (button: string) => this.zone.run(() => this.onKeyPress(button)),
        preventMouseDownDefault: true,
        useMouseEvents: true,
        theme: 'hg-theme-default totem-keyboard',
      })
    })
  }

  private onChange(input: string): void {
    if (this.syncing || !this.target) {
      return
    }

    const applied = this.keyboardService.writeToTarget(input)

    // O campo pode reformatar o valor (máscara de data, telefone). Reflete de
    // volta no teclado para que apagar/continuar digitando parta do texto real.
    if (applied !== input) {
      this.setKeyboardValue(applied)
    }
  }

  private onKeyPress(button: string): void {
    // O paciente pode ter digitado no teclado FISICO (ou num leitor de codigo de
    // barras) entre um toque e outro. O simple-keyboard mantem um buffer proprio:
    // sem realinhar com o campo antes de processar a tecla, ele reescreve o campo
    // com o texto antigo e o que foi digitado por fora some sem aviso.
    this.syncFromTarget()

    if (button === KEY_CLEAR) {
      this.setKeyboardValue('')
      this.keyboardService.writeToTarget('')
      return
    }

    if (button === KEY_SHIFT) {
      this.toggleShift()
      return
    }

    // Shift vale para UMA letra, como no teclado do celular: sem isso ele fica
    // preso em maiuscula e o paciente digita o nome inteiro em caixa alta.
    if (this.keyboard?.options.layoutName === 'shift' && !button.startsWith('{')) {
      this.toggleShift()
    }
  }

  private toggleShift(): void {
    const current = this.keyboard?.options.layoutName
    this.keyboard?.setOptions({ layoutName: current === 'shift' ? 'default' : 'shift' })
  }

  /**
   * Realinha o teclado com o campo antes de cada tecla: o texto (que pode ter
   * mudado por fora) e o ponto de escrita.
   *
   * O ponto de escrita e realinhado SEMPRE, mesmo com o texto igual. Escrever no
   * campo devolve o cursor visivel para o fim, mas o teclado continuava contando
   * a partir de onde o paciente tinha tocado — ele via o cursor piscando no fim
   * e as letras entrando no meio ("Joinville" virava "Joxyinville"). No totem nao
   * ha setas: o fim do texto e o unico ponto de escrita que o paciente consegue
   * enxergar, entao e nele que a escrita acontece.
   */
  private syncFromTarget(): void {
    const atual = this.target?.input.value ?? ''
    if (!this.keyboard) {
      return
    }
    if (this.keyboard.getInput() !== atual) {
      this.setKeyboardValue(atual)
      return
    }
    this.keyboard.setCaretPosition(atual.length)
  }

  /**
   * Escreve o texto no teclado e leva o cursor para o FIM.
   *
   * Mover o cursor nao e detalhe: `setInput` sozinho nao o reposiciona, e a
   * mascara aumenta o texto a cada barra que entra (200 vira 20/0). Com o cursor
   * parado na posicao antiga, a tecla seguinte era inserida no MEIO — digitar
   * 20022004 produzia 20/22/0400. No totem so se digita para frente, entao o fim
   * do texto e sempre o lugar certo.
   */
  private setKeyboardValue(value: string): void {
    if (!this.keyboard) {
      return
    }
    this.syncing = true
    this.keyboard.setInput(value)
    this.keyboard.setCaretPosition(value.length)
    this.syncing = false
  }

  /** Publica a altura real do painel para as telas reservarem espaço. */
  private publishHeight(): void {
    // Espera o painel pintar para medir a altura verdadeira.
    setTimeout(() => {
      const height = this.panel?.nativeElement?.offsetHeight || 0
      this.keyboardService.setHeight(height)
    })
  }

  /** Garante que o campo em preenchimento fique visível acima do teclado. */
  private scrollTargetIntoView(target: VirtualKeyboardTarget): void {
    setTimeout(() => {
      // Sem rolagem suave de proposito: enquanto a pagina desliza, as teclas
      // andam sob o dedo do paciente e o toque cai na tecla vizinha (RC10).
      target.input.scrollIntoView({ block: 'center' })
    }, 150)
  }

  /** `true` enquanto o teclado montado ainda pertence ao host que esta na tela. */
  private isAttachedToCurrentHost(): boolean {
    const host = this.keyboardHost?.nativeElement
    return !!host && host === this.attachedHost && document.body.contains(host)
  }

  private destroyKeyboard(): void {
    this.keyboard?.destroy()
    this.keyboard = undefined
    this.attachedHost = undefined
  }
}
